import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Eye, EyeOff } from 'lucide-react';
import CardView from '@/features/card/CardView';
import { cardFromUser } from '@/features/card/lib/fromUser';
import { themeList, getTheme } from '@/features/card/themes';

const CARD_W = 390; // the width the card is designed for (a phone)

// Full, scrollable card inside a phone frame. The frame's transform makes it the containing block
// for the card's position:fixed bits (save bar, toasts, sheets) so they stay inside the frame.
export function PhonePreview({ card, width = 300 }) {
    const zoom = width / CARD_W;
    const height = Math.round(width * 2.05);
    if (!card) return null;
    return (
        <div
            className="relative mx-auto overflow-hidden rounded-[36px] border-[10px] border-black bg-black shadow-2xl ring-1 ring-[#2a3644]"
            style={{ width: width + 20, height: height + 20, transform: 'translateZ(0)' }}
        >
            <div className="h-full w-full overflow-y-auto overflow-x-hidden overscroll-contain rounded-[26px]">
                <div style={{ width: CARD_W, zoom }}>
                    <CardView card={card} mode="preview" isOwner />
                </div>
            </div>
        </div>
    );
}

const MINI_W = 112;
const MINI_H = 212;
const MINI_SCALE = MINI_W / CARD_W;

// Static thumbnail of the user's card in one theme. Not interactive.
const MiniCard = memo(function MiniCard({ user, themeId, accent, show }) {
    const theme = getTheme(themeId);
    const card = useMemo(() => (show ? cardFromUser(user, { theme: themeId, accentColor: accent }) : null), [show, user, themeId, accent]);
    return (
        <div
            className="relative overflow-hidden rounded-xl"
            style={{ width: MINI_W, height: MINI_H, background: theme.background, pointerEvents: 'none' }}
            aria-hidden="true"
            {...{ inert: '' }}
        >
            {card ? (
                <div
                    style={{
                        width: CARD_W,
                        height: MINI_H / MINI_SCALE,
                        overflow: 'hidden',
                        transform: `scale(${MINI_SCALE})`,
                        transformOrigin: 'top left',
                    }}
                >
                    <CardView card={card} mode="preview" isOwner />
                </div>
            ) : (
                <div className="flex h-full flex-col items-center justify-center gap-2">
                    <span className="h-10 w-10 rounded-full" style={{ background: accent || theme.accents[0], opacity: 0.85 }} />
                    <span className="h-1.5 w-14 rounded-full" style={{ background: accent || theme.accents[0], opacity: 0.5 }} />
                </div>
            )}
        </div>
    );
});

// Only mount the mini cards once the section is (nearly) on screen.
function useNearViewport(ref) {
    const [near, setNear] = useState(false);
    useEffect(() => {
        if (near) return undefined;
        const el = ref.current;
        if (!el) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            setNear(true);
            return undefined;
        }
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setNear(true);
                    io.disconnect();
                }
            },
            { rootMargin: '300px 0px' }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [ref, near]);
    return near;
}

const ThemePicker = ({ user, previewCard, themeId, accent, onThemeChange, onAccentChange }) => {
    const sectionRef = useRef(null);
    const near = useNearViewport(sectionRef);
    const [showPreview, setShowPreview] = useState(false);
    const selected = getTheme(themeId);
    const selectedId = themeList.some((t) => t.id === themeId) ? themeId : 'default';

    return (
        <div ref={sectionRef}>
            <div role="radiogroup" aria-label="Card theme" className="-mx-2 flex snap-x gap-3 overflow-x-auto px-2 pb-3 pt-2">
                {themeList.map((t) => {
                    const active = t.id === selectedId;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            onClick={() => onThemeChange(t.id)}
                            className={`group relative flex-shrink-0 snap-start rounded-2xl p-1.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                                active ? 'bg-primary-50 ring-2 ring-primary-600' : 'ring-1 ring-gray-200 hover:ring-gray-400'
                            }`}
                            style={{ width: MINI_W + 12 }}
                        >
                            <MiniCard user={user} themeId={t.id} accent={active ? accent : null} show={near} />
                            {active && (
                                <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-white shadow ring-2 ring-white">
                                    <Check className="h-4 w-4" />
                                </span>
                            )}
                            <span className={`mt-2 block truncate px-0.5 text-sm font-medium ${active ? 'text-primary-700' : 'text-gray-900'}`}>{t.label}</span>
                            <span className="block px-0.5 text-xs leading-snug text-gray-500 line-clamp-2" style={{ minHeight: '2.5em' }}>
                                {t.description}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-4">
                <p className="text-sm font-medium text-gray-700">Accent color</p>
                <p className="text-xs text-gray-500">Buttons, links and highlights on your {selected.label} card.</p>
                <div role="radiogroup" aria-label="Accent color" className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        role="radio"
                        aria-checked={!accent}
                        onClick={() => onAccentChange(null)}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                            !accent ? 'border-primary-600 bg-primary-50 text-primary-700 ring-1 ring-primary-600' : 'border-gray-300 text-gray-700 hover:border-gray-400'
                        }`}
                    >
                        <span className="h-4 w-4 rounded-full ring-1 ring-black/10" style={{ background: selected.accents[0] }} />
                        Theme default
                    </button>
                    {selected.accents.map((hex) => {
                        const on = accent && accent.toLowerCase() === hex.toLowerCase();
                        return (
                            <button
                                key={hex}
                                type="button"
                                role="radio"
                                aria-checked={!!on}
                                aria-label={`Accent ${hex}`}
                                title={hex}
                                onClick={() => onAccentChange(hex)}
                                className={`relative h-9 w-9 rounded-full ring-1 ring-black/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                                    on ? 'ring-2 ring-primary-600 ring-offset-2' : 'hover:scale-110'
                                }`}
                                style={{ background: hex }}
                            >
                                {on && <Check className="absolute inset-0 m-auto h-4 w-4 text-white mix-blend-difference" />}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Phones/tablets: the side preview is hidden, so offer it inline */}
            <div className="mt-5 lg:hidden">
                <button
                    type="button"
                    onClick={() => setShowPreview((v) => !v)}
                    aria-expanded={showPreview}
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                    {showPreview ? <EyeOff className="mr-2 h-4 w-4" /> : <Eye className="mr-2 h-4 w-4" />}
                    {showPreview ? 'Hide preview' : 'Preview with these changes'}
                </button>
                {showPreview && (
                    <div className="mt-4">
                        <PhonePreview card={previewCard} width={280} />
                        <p className="mt-3 text-center text-xs text-gray-500">Not saved yet — tap Save Changes to publish.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ThemePicker;
